// Draft contracts only. Pricing rates and costing formulas remain server owned.
export const finiteDecimal = (value) => {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim())) return null;
  const numeric = Number(value);
  if (numeric === 0 && typeof value === "string" && /[1-9]/.test(value.split(/[eE]/)[0])) return null;
  return Number.isFinite(numeric) ? numeric : null;
};

// Service-boundary validation only: never repair or recalculate quoted costs.
export const validatedFbShouldCost = (quote) => {
  const fields = ["MaterialCost", "ProcessCost", "PackagingCost", "OHPCost", "TotalCost"];
  if (!quote || typeof quote !== "object" || Array.isArray(quote)) {
    throw new Error("The pricing service did not return a complete F&B quote. No price was generated.");
  }
  const values = fields.map((field) => finiteDecimal(quote[field]));
  if (values.some((value) => value === null || value < 0)) {
    throw new Error("The pricing service returned missing or invalid F&B cost amounts. No price was generated.");
  }
  // Match the priced SQL success contract (50143), without requiring optional
  // process, packaging or overhead components to be nonzero.
  if (values[0] <= 0 || values[4] <= 0) {
    throw new Error("The pricing service returned nonpositive F&B material or total cost. No price was generated.");
  }
  const subtotal = values.slice(0, 4).reduce((sum, value) => sum + value, 0);
  // Four independently rounded buckets plus a rounded total: at most 5 half
  // units in the fourth decimal place. Floating-point epsilon is separate.
  const tolerance = 5 * 0.00005 + Number.EPSILON * Math.max(1, subtotal, values[4]) * 8;
  if (!Number.isFinite(subtotal) || Math.abs(subtotal - values[4]) > tolerance) {
    throw new Error("The pricing service returned F&B costs that do not reconcile with the total. No price was generated.");
  }
  return Object.fromEntries(fields.map((field, index) => [field, values[index]]));
};

export const fbSkuUnitError = (draft, commodity = "Cookies") => {
  if (!["g", "kg", "ml", "L"].includes(draft?.skuWeightUnit)) return "Select an explicit SKU unit.";
  if (!["ml", "L"].includes(draft.skuWeightUnit)) return "";
  const name = commodity.replace(/[\s_-]/g, "").toUpperCase();
  if (name === "COOKIES" || (name === "ICECREAMS" && !["Standard", "Premium"].includes(draft.marketPositioning))) {
    return "Enter grams or kilograms; no density is configured for this product and tier.";
  }
  return "";
};

// Temporary input availability, not a change to labor rates or costing rules.
export const FB_PRICING_COUNTRIES = Object.freeze([
  "USA", "Canada", "Mexico", "Germany", "Japan", "India", "China",
]);

export const usesSupportedFbCountries = (commodity = "Cookies") =>
  ["COOKIES", "ICECREAMS"].includes(String(commodity).replace(/[\s_-]/g, "").toUpperCase());

export const fbCountryError = (country, commodity = "Cookies") => {
  if (!usesSupportedFbCountries(commodity)) return "";
  if (!country) return "Select one of the seven supported manufacturing countries.";
  return FB_PRICING_COUNTRIES.includes(country) ? ""
    : `${country} is not currently available for F&B pricing. Select USA, Canada, Mexico, Germany, Japan, India or China to continue.`;
};

export const isValidFbProduct = (draft, commodity = "Cookies") => Boolean(
  draft?.productName?.trim() && draft?.marketPositioning && draft?.annualVolume &&
  draft?.manufacturingCountry && finiteDecimal(draft?.skuWeight) > 0 &&
  !fbSkuUnitError(draft, commodity) && !fbCountryError(draft?.manufacturingCountry, commodity)
);

export const normalizeFormulationRows = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
  ingredient: row?.ingredient ?? row?.Ingredient ?? "",
  percentage: row?.percentage ?? row?.Percentage ?? row?.Input ?? row?.input ?? "",
  functional_group: row?.functional_group ?? row?.FunctionalGroup ?? row?.functionalGroup ?? null,
  ingredientId: String(row?.ingredientId ?? row?.IngredientID ?? ""),
  pricedIngredient: row?.pricedIngredient ?? row?.Ingredient ?? row?.ingredient ?? "",
  ingredientCost: row?.ingredientCost ?? row?.IngredientCost,
  moisturePer: row?.moisturePer ?? row?.MoisturePer,
  premixLoss: row?.premixLoss ?? row?.PremixLoss,
}));

export const buildFormulationJsonFromRows = (rows, previous = []) => {
  const byId = new Map((Array.isArray(previous) ? previous : []).map((row) => [String(row?.IngredientID ?? row?.ingredientId ?? ""), row]));
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const identity = String(row?.ingredientId ?? row?.IngredientID ?? "");
    const previousRow = identity ? byId.get(identity) || {} : {};
    return {
      ...previousRow,
      IngredientID: identity || undefined,
      Ingredient: row.ingredient,
      PricedIngredient: row.pricedIngredient ?? previousRow.PricedIngredient ?? previousRow.Ingredient,
      Input: finiteDecimal(row.percentage),
      FunctionalGroup: row.functional_group,
      IngredientCost: row.ingredientCost ?? previousRow.IngredientCost,
      MoisturePer: row.moisturePer ?? previousRow.MoisturePer,
      PremixLoss: row.premixLoss ?? previousRow.PremixLoss,
    };
  });
};

export const recipeValidationError = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return "At least one priced recipe ingredient is required.";
  let total = 0;
  const identities = new Set();
  for (const row of rows) {
    if (!row.ingredientId || identities.has(row.ingredientId) || !row.ingredient?.trim() || row.ingredient.trim() !== row.pricedIngredient?.trim()) {
      return "New or renamed ingredients need refreshed pricing. Restore a priced ingredient or generate a fresh recipe.";
    }
    identities.add(row.ingredientId);
    const input = finiteDecimal(row.percentage), rate = finiteDecimal(row.ingredientCost);
    const moisture = finiteDecimal(row.moisturePer), premix = finiteDecimal(row.premixLoss);
    if (input === null || input < 0 || input > 100 || rate === null || rate < 0 || moisture === null || moisture < 0 || moisture > 100 || premix === null || premix < 0 || premix >= 100) {
      return "Every ingredient needs valid composition, price, moisture and premix loss.";
    }
    total += input;
  }
  return Math.abs(total - 100) <= rows.length * 0.005 + 1e-8 ? "" : "Edited recipe composition must total 100%.";
};
